import React from "react";
import {
  ButtonType,
  CantPlay,
  CantPlayAction,
  SpeakingRate,
  PlaybackState,
} from "../../common/api";
import { FlexRow } from "../../common/CommonStyles";
import { AnswerCaptureAudioOnlyUplink, Uplink } from "./Uplink";
import { WithTranslation } from "react-i18next";

export interface LessonPlayer {
  onLessonPlayingStateUpdated: (isPlaying: boolean) => void | null;
  onCanPauseUpdated: (canPause: boolean) => void | null;
  onShowListeningUI: (stream: MediaStream) => void | null;
  onHideListeningUI: () => void | null;
  onVoiceActivity: (detected: boolean) => void | null;
  onAnswerCaptureStarted: () => void | null;
  onAnswerCaptureDidNotStart: () => void | null;
  onAudioVideoCaptureDidNotStart: () => void | null;
  onAnswerCaptureStopped: () => void | null;
  onLessonStopped?: (
    showPostLessonScreen: boolean,
    cantPlay: CantPlay | null
  ) => void;
  onPlayConfetti: () => void | null;
  onWaitTime: (time: number, buttonType: ButtonType) => void | null;
  onCaptionFull: (text: string) => void | null;
  onCaptionProgress: (text: string) => void | null;
  getUserVariablesForStartPlaying?: () => Record<string, [string, string]>;
}

const makeDeferred = () => {
  const deferred = {} as any;
  deferred.promise = new Promise((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
};

interface Props extends Pick<WithTranslation, "t"> {
  profileId: number;
  lessonPlayer: LessonPlayer;
  deleteLessonState: () => void;
  shouldHideVideo: boolean;
  getJwt: () => string;
  webSocketUrl: () => string;
  speakingRate: SpeakingRate;
  supportsClosedCaptions: boolean;
  closedCaptionsEnabled: boolean;
  playbackState: React.MutableRefObject<PlaybackState>;
}
interface State {}

class Connection {
  webSocket: WebSocket | null = null;
  rtc: RTCPeerConnection | null = null;
  authenticated: boolean = false;
  incomingQueuedMessages: string[] = [];
  outgoingDataChannelQueuedMessages: string[] = [];
  dataChannel: RTCDataChannel | null = null;
  dataChannelReady: boolean = false;
}

export class WebRtcPlayer extends React.Component<Props, State> {
  peerConnectionOptions = {
    optional: [{ DtlsSrtpKeyAgreement: true }],
  };
  mediaConstraints = {
    mandatory: {
      OfferToReceiveAudio: true,
      OfferToReceiveVideo: true,
    },
  };

  speakingRate: SpeakingRate;
  currentConnection: Connection | null = null;
  stream: MediaStream | null = null;
  shouldBeConnected = false;
  lastStartPlayingParams: {
    type: string;
    version: number | null;
    lesson_id: number;
    custom_flashpack_id: number | null;
    unit_id: number | null;
    node_uuid: string;
    single_node: boolean;
    user_variables: Record<string, [string, string]>;
    profile_id: number;
    speaking_rate?: number;
    temporary_first_name?: string;
    closed_captions?: boolean;
  } | null = null;
  lastLessonStateOrder = -1;
  lastNodePlayedOrder = -1;
  uplink: Uplink;
  waitContinueTimeoutId: number | null = null;
  waitDisconnectTimeoutId: number | null = null;

  // Tracks whether the peer has signaled an active answer capture
  // (start_answer_capture without a matching stop_answer_capture).
  // closeConnection consults this so onAnswerCaptureStopped only fires
  // when there was actually a capture to stop.
  isAnswerCaptureActive = false;

  // We don't actually want to re-render on unmute, just update the video node.
  // But if a re-render occurs, we need to know the right state.
  isMuted = true;

  connectionAvailabilityDeferred = makeDeferred();
  connectionAvailabilityResolved = false;

  constructor(props: Props) {
    super(props);
    this.state = {};
    this.speakingRate = props.speakingRate;
  }

  createPeerConnection = (connection, pcConfig) => {
    try {
      connection.rtc = new (RTCPeerConnection as any)(
        pcConfig,
        this.peerConnectionOptions
      );
      connection.rtc.onicecandidate = (event) => {
        if (event.candidate) {
          const candidate = {
            type: "candidate",
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            sdpMid: event.candidate.sdpMid,
            candidate: event.candidate.candidate,
          };
          (connection.dataChannelReady
            ? this.sendToPeerViaDataChannel
            : this.sendToPeerViaWebSocket)(
            connection,
            JSON.stringify(candidate)
          );
        } else {
        }
      };
      connection.rtc.ondatachannel = (event) => {
        connection.dataChannel = event.channel;
        connection.dataChannel.onmessage = (event) => {
          this.onDataChannelMessage(connection, event);
        };
        connection.dataChannel.onopen = () => {
          this.onDataChannelOpened(connection);
        };
        connection.dataChannel.onclose = () => {
          this.onDataChannelClosed(connection);
        };
      };
      connection.rtc.onnegotiationneeded = () =>
        this.onNegotiationNeeded(connection);
      connection.rtc.onconnecting = this.onSessionConnecting;
      connection.rtc.onopen = this.onSessionOpened;
      connection.rtc.ontrack = this.onTrack;
      connection.rtc.onremovetrack = this.onRemoveTrack;
      connection.rtc.onconnectionstatechange = (event) => {
        if (
          connection.rtc != null &&
          (connection.rtc.connectionState === "disconnected" ||
            connection.rtc.connectionState === "failed" ||
            connection.rtc.connectionState === "closed")
        ) {
          connection.rtc = null;
          this.closeConnection(connection);
        }
      };
    } catch (e) {}
  };

  onDataChannelOpened = (connection: Connection) => {
    connection.dataChannelReady = true;
    this.sendToPeerViaDataChannel(
      connection,
      JSON.stringify({ type: "upgrade" })
    );
    connection.outgoingDataChannelQueuedMessages.forEach((msg) => {
      this.sendToPeerViaDataChannel(connection, msg);
    });
    connection.outgoingDataChannelQueuedMessages = [];
  };

  onDataChannelClosed = (connection: Connection) => {
    connection.dataChannelReady = false;
    this.closeConnection(connection);
  };

  onDataChannelMessage = (connection: Connection, event) => {
    this.handlePeerMessage(connection, event.data);
  };

  onRemoveTrack = (_event) => {
    (document.getElementById("play") as HTMLButtonElement).disabled = true;
  };

  onTrack = (event) => {
    const remoteVideoElement = document.getElementById(
      "remote-video"
    ) as HTMLVideoElement;
    if (event.track.kind === "video") {
      remoteVideoElement.srcObject = event.streams[0];
      remoteVideoElement.play().catch((reason) => {});
    }
    return false;
  };

  onRemoteSdpSuccess = () => {};
  onRemoteSdpError = (event) => {};

  onSldFailure = () => {};

  onAidSuccess = () => {};
  onAidFailure = () => {};

  handlePeerMessage = (connection, data) => {
    const dataJson = JSON.parse(data);

    if (dataJson.type === "status") {
      if (dataJson.status !== "ok") {
        this.shouldBeConnected = false;
      } else if (dataJson.server_busy === true) {
        this.shouldBeConnected = false;
        const cantPlay: CantPlay = {
          title: this.props.t("server_busy_dialog_title"),
          description: this.props.t("server_busy_dialog_message"),
          image_url: `${process.env.PUBLIC_URL}/teacher_busy.png`,
          action: CantPlayAction.GoBackToLessonDetails,
        };
        this.props.lessonPlayer.onLessonStopped?.(false, cantPlay);
      }
      return;
    }

    if (connection.rtc == null && dataJson.type !== "offer") {
      connection.incomingQueuedMessages.push(data);
      return;
    }

    if (dataJson.type === "offer") {
      if (connection.rtc === null) {
        this.createPeerConnection(connection, dataJson.pcConfig);
      }
      delete dataJson.pcConfig;
      // Sequence: setRemoteDescription -> createAnswer -> setLocalDescription
      // -> send answer -> drain queued messages. Per RFC 8829, the answerer
      // must install the local description before signaling the answer
      // back, otherwise an SLD failure leaves the peer holding an answer
      // we can't honor. createAnswer also requires remote SDP to already
      // be installed, so it can't run in parallel with setRemoteDescription.
      // Drain happens after SLD because addIceCandidate only needs the
      // remote description (already set), so we could drain earlier — but
      // putting it after SLD keeps the success-path linearization simple.
      connection.rtc
        .setRemoteDescription(new RTCSessionDescription(dataJson))
        .then(() => {
          this.onRemoteSdpSuccess();
          return connection.rtc.createAnswer(this.mediaConstraints);
        })
        .then((sessionDescription) => {
          // Nest the SLD chain so its own .catch handles onSldFailure
          // without leaking to the outer .catch (which is for SRD/answer
          // errors only). Send + drain run only on SLD success.
          return connection.rtc
            .setLocalDescription(sessionDescription)
            .then(() => {
              const data = JSON.stringify(sessionDescription);
              (connection.dataChannelReady
                ? this.sendToPeerViaDataChannel
                : this.sendToPeerViaWebSocket)(connection, data);
              const queued = connection.incomingQueuedMessages;
              connection.incomingQueuedMessages = [];
              queued.forEach((msg) => this.handlePeerMessage(connection, msg));
            })
            .catch((error) => {
              this.onSldFailure();
            });
        })
        .catch((error) => {
          // setRemoteDescription or createAnswer failure lands here.
          // setLocalDescription errors are swallowed by the inner .catch.
          this.onRemoteSdpError(error);
        });
    } else if (dataJson.type === "answer") {
      // We get this after adding tracks midstream and sending renegotiation offer to peer
      connection.rtc
        .setRemoteDescription(new RTCSessionDescription(dataJson))
        .then(() => {
          this.uplink.rtcSetRemoteDescriptionSucceeded();
        })
        .catch((err) => {
          this.uplink.rtcSetRemoteDescriptionFailed();
        });
      // TODO: change msg handling to classes like Node etc
    } else if (dataJson.type === "state") {
      if (dataJson.order > this.lastLessonStateOrder) {
        this.props.lessonPlayer.onLessonPlayingStateUpdated?.(
          dataJson.is_playing
        );
        if (dataJson.is_playing) {
          this.props.lessonPlayer.onCanPauseUpdated?.(dataJson.can_pause);
        }
        this.lastLessonStateOrder = dataJson.order;
        if (
          this.currentConnection &&
          !this.props.playbackState.current.bouncingStates &&
          ((this.props.playbackState.current.startPlayingMark !== null &&
            Date.now() - this.props.playbackState.current.startPlayingMark >
              10 * 1000) ||
            !!dataJson.ruid)
        ) {
          this.props.playbackState.current.bouncingStates = true;
        }
        if (this.props.playbackState.current.bouncingStates) {
          this.sendToPeerViaDataChannel(
            this.currentConnection,
            JSON.stringify({ type: "state", state: dataJson.state })
          );
        }
      }
      if (!!dataJson.ruid) {
        this.sendToPeerViaDataChannel(
          this.currentConnection,
          JSON.stringify({
            type: "user_input",
            ruid: dataJson.ruid,
            user_input: {
              name: prompt(this.props.t("request_user_input")),
              phonemes: null,
            },
          })
        );
      }
    } else if (dataJson.type === "ready") {
      if (this.lastStartPlayingParams !== null) {
        this.sendToPeerViaDataChannel(
          this.currentConnection,
          JSON.stringify({
            ...this.lastStartPlayingParams,
            speaking_rate: this.speakingRate,
          })
        );
        if (this.currentConnection) {
          this.props.playbackState.current.sentStartPlaying = true;
          this.props.playbackState.current.startPlayingMark = Date.now();
        }
      }
      if (connection === this.currentConnection) {
        this.connectionAvailabilityDeferred.resolve();
        this.connectionAvailabilityResolved = true;
      }
    } else if (dataJson.type === "node_played") {
      if (dataJson.order > this.lastNodePlayedOrder) {
        this.lastNodePlayedOrder = dataJson.order;
      }
    } else if (dataJson.type === "start_answer_capture") {
      // Uplink itself will call whoever needs to know
      // that the uplink media stream(s) have started
      this.isAnswerCaptureActive = true;
      this.uplink.startCapturingMedia(dataJson.answer_capture_id);
    } else if (dataJson.type === "stop_answer_capture") {
      this.isAnswerCaptureActive = false;
      this.uplink.stopCapturingMedia();
      this.props.lessonPlayer.onAnswerCaptureStopped?.();
    } else if (dataJson.type === "stopped_playing") {
      if (this.uplink) {
        this.uplink.stop();
      }
      // Lesson/block finished - we don't want to resume from state
      // when a reconnect happens (which will happen after we auto-disconnect
      // after being idle, then press Play again)
      this.lastStartPlayingParams = null;
      this.props.lessonPlayer.onLessonStopped?.(
        dataJson.show_post_lesson_screen,
        dataJson.cant_play
      );
    } else if (dataJson.type === "clip") {
      if (dataJson.clip === "HOORAY") {
        this.props.lessonPlayer.onPlayConfetti?.();
      }
    } else if (dataJson.type === "caption_full") {
      this.props.lessonPlayer.onCaptionFull?.(dataJson.text);
    } else if (dataJson.type === "caption_progress") {
      this.props.lessonPlayer.onCaptionProgress?.(dataJson.text);
    } else if (dataJson.type === "vad") {
      this.props.lessonPlayer.onVoiceActivity?.(dataJson.detected);
    } else if (dataJson.type === "wait_time") {
      this.clearWaitTimeouts();
      if (dataJson.time_remaining > 0) {
        this.waitContinueTimeoutId = window.setTimeout(
          this.readyToContinue,
          dataJson.time_remaining * 1000
        );
      }
      if (dataJson.disconnect_time_sec) {
        this.waitDisconnectTimeoutId = window.setTimeout(
          this.disconnect,
          dataJson.disconnect_time_sec * 1000
        );
      }
      this.props.lessonPlayer.onWaitTime?.(
        dataJson.time_remaining,
        dataJson.button_type
      );
    } else if (dataJson.type === "candidate") {
      const candidate = new RTCIceCandidate({
        sdpMLineIndex: dataJson.sdpMLineIndex,
        sdpMid: dataJson.sdpMid,
        candidate: dataJson.candidate,
      });
      connection.rtc
        .addIceCandidate(candidate)
        .then(() => this.onAidSuccess())
        .catch((e) => {});
    }
  };

  signIn = (is_reconnect: boolean) => {
    // Between scheduling (e.g. the reconnect setTimeout in closeConnection)
    // and this firing, the user can disconnect — bail instead of opening a
    // fresh socket nobody asked for.
    if (!this.shouldBeConnected) {
      return;
    }
    const connection = new Connection();
    this.lastNodePlayedOrder = -1;
    this.lastLessonStateOrder = -1;
    this.currentConnection = connection;
    connection.webSocket = new WebSocket(this.props.webSocketUrl());
    connection.webSocket.addEventListener("open", (event) => {
      if (connection.webSocket?.readyState !== WebSocket.OPEN) {
        return;
      }
      connection.authenticated = true;
      connection.webSocket?.send(
        JSON.stringify({
          type: "hello",
          version: 9,
          jwt: this.props.getJwt(),
          seconds_from_gmt: new Date().getTimezoneOffset() * -60,
          is_reconnect,
        })
      );
    });
    connection.webSocket.addEventListener("message", (event) => {
      this.handlePeerMessage(connection, event.data);
    });
    connection.webSocket.addEventListener("close", (event) => {
      connection.webSocket = null;
      if (!connection.dataChannelReady) {
        this.closeConnection(connection);
      }
    });
  };

  sendToPeerViaWebSocket = (connection: Connection | null, data) => {
    if (
      connection != null &&
      connection.webSocket != null &&
      connection.webSocket.readyState === WebSocket.OPEN &&
      connection.authenticated
    ) {
      connection.webSocket!.send(data);
    }
  };

  sendToPeerViaDataChannel = (connection: Connection | null, data) => {
    if (connection != null && connection.authenticated) {
      if (connection.dataChannelReady) {
        connection.dataChannel!.send(data);
      } else {
        connection.outgoingDataChannelQueuedMessages.push(data);
      }
    }
  };

  closeConnection = (connection: Connection) => {
    if (connection.dataChannel !== null) {
      connection.dataChannel.close();
    }
    if (connection.rtc !== null) {
      connection.rtc.close();
    }
    if (connection.webSocket != null) {
      connection.webSocket.close();
    }
    connection.authenticated = false;

    if (this.currentConnection === connection) {
      if (this.uplink) {
        this.uplink.stop();
      }
      // Only signal a capture-stop if there was actually a capture in
      // progress — otherwise we spam the host with stop events on every
      // teardown, even ones that happened outside any answer phase.
      if (this.isAnswerCaptureActive) {
        this.isAnswerCaptureActive = false;
        this.props.lessonPlayer.onAnswerCaptureStopped();
      }
      this.currentConnection = null;
      // Always recreate the deferred — the old one represents "this
      // connection became available," which is now false. If it was
      // still pending, resolve it first so awaiters wake instead of
      // hanging forever; they'll see currentConnection=null and the
      // downstream uplink.start() will fail cleanly (see initConnection).
      if (!this.connectionAvailabilityResolved) {
        this.connectionAvailabilityDeferred.resolve();
      }
      this.connectionAvailabilityDeferred = makeDeferred();
      this.connectionAvailabilityResolved = false;
      if (this.shouldBeConnected) {
        setTimeout(this.signIn, 1000, true);
      }
    }
  };

  disconnect = () => {
    // Pausing the video will keep the last frame visible, rather than a black
    // rectangle, when the player is disconnected.
    (document.getElementById("remote-video") as HTMLVideoElement).pause();
    this.shouldBeConnected = false;
    if (this.currentConnection != null) {
      this.closeConnection(this.currentConnection);
    }
    if (this.uplink) {
      this.uplink.stop();
    }
    this.clearWaitTimeouts();
    this.lastStartPlayingParams = null;
  };

  setSpeakingRate = (speakingRate: SpeakingRate) => {
    this.speakingRate = speakingRate;
    this.sendToPeerViaDataChannel(
      this.currentConnection,
      JSON.stringify({
        type: "set_speaking_rate",
        rate: speakingRate,
      })
    );
  };

  clearWaitTimeouts = () => {
    if (this.waitContinueTimeoutId) {
      clearTimeout(this.waitContinueTimeoutId);
      this.waitContinueTimeoutId = null;
    }
    if (this.waitDisconnectTimeoutId) {
      clearTimeout(this.waitDisconnectTimeoutId);
      this.waitDisconnectTimeoutId = null;
    }
  };

  cleanUp = () => {
    this.disconnect();
    return this.props.deleteLessonState();
  };

  // Re-negotiation happens whenever tracks are added or removed
  // AFTER the initial offer/answer exchange happened.
  // Based on https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/negotiationneeded_event
  onNegotiationNeeded = (connection) => {
    if (connection.rtc == null) {
      return;
    }

    // Create a new offer, install it as local description, then signal
    // it. Per RFC 8829, the offerer must install local SDP before sending
    // the offer — otherwise an SLD failure leaves the peer holding an
    // offer we can't honor.
    connection.rtc
      .createOffer()
      .then((offer) => {
        return connection.rtc
          .setLocalDescription(offer)
          .then(() => {
            this.sendToPeerViaDataChannel(connection, JSON.stringify(offer));
          })
          .catch((error) => {
            this.onSldFailure();
          });
      })
      .catch((error) => {});
  };

  onSessionConnecting = (_message) => {};
  onSessionOpened = (_message) => {};

  stopCapturingMedia = () => {
    if (this.uplink) {
      this.uplink.stopCapturingMedia();
    }
  };

  ensureConnection = async () => {
    if (!this.shouldBeConnected) {
      this.shouldBeConnected = true;
      this.signIn(false);
    }

    await this.connectionAvailabilityDeferred.promise;
  };

  initConnection = async (): Promise<boolean> => {
    // Recreate the uplink object every time
    if (this.uplink) {
      this.uplink.stop();
    }

    // Must be connected to webrtc before we attempt to establish an uplink
    await this.ensureConnection();

    // After the await, the user may have disconnected — or the connection
    // we were waiting for may have been torn down (closeConnection
    // force-resolves the deferred so we don't hang here, but it clears
    // currentConnection at the same time). Either way, no uplink to wire.
    if (!this.shouldBeConnected || this.currentConnection === null) {
      return false;
    }

    this.uplink = new AnswerCaptureAudioOnlyUplink(
      this.props.lessonPlayer,
      () => this.currentConnection?.rtc
    );

    if (!(await this.uplink.start())) {
      return false;
    }

    // uplink.start() can take a while (mic permission, etc.) — re-check
    // that the connection is still around. If not, undo the uplink we
    // just brought up so it doesn't keep capturing against a dead pc.
    if (!this.shouldBeConnected || this.currentConnection === null) {
      this.uplink.stop();
      return false;
    }

    this.isMuted = false;
    (document.getElementById("remote-video") as HTMLVideoElement).muted = false;
    (document.getElementById("remote-video") as HTMLVideoElement).play();
    return true;
  };

  playLesson = async (
    lessonId: number,
    customFlashpackId: number | null,
    unitId: number | undefined,
    lessonVersion: number | null,
    nodeUuid: string,
    playSingleNode: boolean,
    ignoreState: boolean,
    askToResume: boolean,
    temporaryFirstName?: string
  ) => {
    this.clearWaitTimeouts();
    if (!(await this.initConnection())) {
      // Uplink failed to start (mic blocked, no connection, etc.).
      // Bail before sending start_playing — it depends on a live
      // connection that wouldn't exist.
      return;
    }

    // We should resume from state if we get disconnected
    // while playing this block or lesson
    this.lastStartPlayingParams = {
      type: "start_playing",
      version: lessonVersion,
      lesson_id: lessonId,
      custom_flashpack_id: customFlashpackId,
      unit_id: unitId || null,
      node_uuid: nodeUuid,
      single_node: playSingleNode,
      user_variables:
        this.props.lessonPlayer.getUserVariablesForStartPlaying?.() || {},
      profile_id: this.props.profileId,
      temporary_first_name: temporaryFirstName,

      ...(this.props.supportsClosedCaptions
        ? { closed_captions: this.props.closedCaptionsEnabled }
        : {}),
    };

    this.sendToPeerViaDataChannel(
      this.currentConnection,
      JSON.stringify({
        ...this.lastStartPlayingParams,
        ignore_state: ignoreState,
        ask_to_resume: askToResume,
        speaking_rate: this.speakingRate,
      })
    );

    if (this.currentConnection) {
      this.props.playbackState.current.sentStartPlaying = true;
      this.props.playbackState.current.startPlayingMark = Date.now();
    }
  };

  readyToContinue = () => {
    this.clearWaitTimeouts();
    this.sendToPeerViaDataChannel(
      this.currentConnection,
      JSON.stringify({ type: "ready_to_continue" })
    );
  };

  keyDown = (e) => {
    this.sendToPeerViaDataChannel(
      this.currentConnection,
      JSON.stringify({ type: "key", key: e.key })
    );
  };

  componentDidMount() {
    this.shouldBeConnected = true;
    this.signIn(false);

    window.addEventListener("beforeunload", this.cleanUp);
    window.addEventListener("keydown", this.keyDown);
  }

  componentWillUnmount() {
    this.cleanUp();
    window.removeEventListener("keydown", this.keyDown);
    window.removeEventListener("beforeunload", this.cleanUp);
  }

  render() {
    return (
      <FlexRow
        style={{
          justifyContent: "center",
          position: "relative",
          display: this.props.shouldHideVideo ? "none" : "",
        }}
      >
        <video
          playsInline
          style={{
            borderRadius: "28px",
            width: "calc(100% - 30px)",
            maxHeight: "70%",
          }}
          id="remote-video"
          muted={this.isMuted /* Enables autoplay. Removed on interaction. */}
          onMouseDown={(e) => {
            this.sendToPeerViaDataChannel(
              this.currentConnection,
              JSON.stringify({
                type: "touch",
                x: e.nativeEvent.offsetX / e.currentTarget.clientWidth,
                y: e.nativeEvent.offsetY / e.currentTarget.clientHeight,
              })
            );
          }}
        />
      </FlexRow>
    );
  }
}
